import React, { useState } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Tag,
  Dropdown,
  ColorPicker,
  Row,
  Col,
  Typography,
  Badge,
  Spin,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  TagOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  StopOutlined,
  CopyOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  missionTypeService,
  MissionType,
  MissionTypeFormData,
  MissionTypeFilters,
} from "../../services/missionTypeService";
import CustomFieldBuilder from "../../components/CustomFieldBuilder";

const { Text } = Typography;
const { TextArea } = Input;

const AdminMissionTypes: React.FC = () => {
  const [filters, setFilters] = useState<MissionTypeFilters>({
    page: 1,
    limit: 10,
    sort_by: "name",
    sort_order: "ASC",
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingType, setEditingType] = useState<MissionType | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Requête pour récupérer les types de missions
  const {
    data: missionTypesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["mission-types", filters],
    queryFn: () => missionTypeService.getMissionTypes(filters),
  });

  // Requête pour les statistiques
  const { data: statsData } = useQuery({
    queryKey: ["mission-types-stats"],
    queryFn: () => missionTypeService.getMissionTypeStats(),
  });

  // Mutation pour créer un type de mission
  const createMutation = useMutation({
    mutationFn: (data: MissionTypeFormData) =>
      missionTypeService.createMissionType(data),
    onSuccess: () => {
      message.success("Type de mission créé avec succès");
      setModalVisible(false);
      form.resetFields();
      setEditingType(null);
      queryClient.invalidateQueries({ queryKey: ["mission-types"] });
      queryClient.invalidateQueries({ queryKey: ["mission-types-stats"] });
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la création"
      );
    },
  });

  // Mutation pour modifier un type de mission
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<MissionTypeFormData>;
    }) => missionTypeService.updateMissionType(id, data),
    onSuccess: () => {
      message.success("Type de mission modifié avec succès");
      setModalVisible(false);
      setEditingType(null);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ["mission-types"] });
      queryClient.invalidateQueries({ queryKey: ["mission-types-stats"] });
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la modification"
      );
    },
  });

  // Mutation pour supprimer un type de mission
  const deleteMutation = useMutation({
    mutationFn: (id: string) => missionTypeService.deleteMissionType(id),
    onSuccess: () => {
      message.success("Type de mission supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["mission-types"] });
      queryClient.invalidateQueries({ queryKey: ["mission-types-stats"] });
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la suppression"
      );
    },
  });

  // Mutation pour activer/désactiver un type de mission
  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => missionTypeService.toggleMissionTypeStatus(id),
    onSuccess: (data) => {
      message.success(
        `Type de mission ${data.is_active ? "activé" : "désactivé"}`
      );
      queryClient.invalidateQueries({ queryKey: ["mission-types"] });
      queryClient.invalidateQueries({ queryKey: ["mission-types-stats"] });
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la modification"
      );
    },
  });

  // Mutation pour dupliquer un type de mission
  const duplicateMutation = useMutation({
    mutationFn: (id: string) => missionTypeService.duplicateMissionType(id),
    onSuccess: () => {
      message.success("Type de mission dupliqué avec succès");
      queryClient.invalidateQueries({ queryKey: ["mission-types"] });
      queryClient.invalidateQueries({ queryKey: ["mission-types-stats"] });
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la duplication"
      );
    },
  });

  const missionTypes = missionTypesData?.data?.mission_types || [];
  const pagination = missionTypesData?.data?.pagination;
  const stats = statsData;

  const handleCreate = () => {
    setEditingType(null);
    form.resetFields();
    form.setFieldsValue({
      color: missionTypeService.getDefaultColor(),
      base_price: 25.0,
      price_per_km: 0.85,
      estimated_duration: 120,
      requires_special_vehicle: false,
      vehicle_types: ["camionnette"],
      custom_fields: [],
      is_active: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: MissionType) => {
    setEditingType(record);
    form.setFieldsValue({
      ...record,
      color: record.color || "#1890ff",
      custom_fields: record.custom_fields || [],
    });
    setModalVisible(true);
  };

  const handleDelete = (id: string, name: string) => {
    Modal.confirm({
      title: "Supprimer le type de mission",
      content: `Êtes-vous sûr de vouloir supprimer "${name}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleToggleStatus = (record: MissionType) => {
    toggleStatusMutation.mutate(record.id);
  };

  const handleDuplicate = (id: string) => {
    duplicateMutation.mutate(id);
  };

  const handleSubmit = async (values: any) => {
    // Validation côté client
    const validationErrors = missionTypeService.validateMissionTypeData(values);
    if (validationErrors.length > 0) {
      message.error(validationErrors.join(", "));
      return;
    }

    try {
      if (editingType) {
        await updateMutation.mutateAsync({ id: editingType.id, data: values });
      } else {
        await createMutation.mutateAsync(values);
      }
    } catch (error) {
      // Erreur déjà gérée dans les mutations
    }
  };

  const handleTableChange = (
    newPagination: any,
    _filters: any,
    sorter: any
  ) => {
    const newFilters: MissionTypeFilters = {
      ...filters,
      page: newPagination.current,
      limit: newPagination.pageSize,
    };

    if (sorter.field) {
      newFilters.sort_by = sorter.field;
      newFilters.sort_order = sorter.order === "ascend" ? "ASC" : "DESC";
    }

    setFilters(newFilters);
  };

  const handleSearch = (value: string) => {
    setFilters({
      ...filters,
      search: value || undefined,
      page: 1,
    });
  };

  const handleStatusFilter = (value: string) => {
    setFilters({
      ...filters,
      is_active: value === "all" ? undefined : value === "active",
      page: 1,
    });
  };

  const vehicleTypeOptions = missionTypeService.getVehicleTypeOptions();

  const columns: ColumnsType<MissionType> = [
    {
      title: "Type de mission",
      key: "name",
      sorter: true,
      render: (_, record) => (
        <Space>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: record.color,
              borderRadius: "50%",
            }}
          />
          <div>
            <div style={{ fontWeight: "bold" }}>{record.name}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {record.description}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Tarification",
      key: "pricing",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      render: (_, record) => (
        
        <Space direction="vertical" size="small">
          <div>
            <DollarOutlined style={{ marginRight: "4px" }} />
            Base: {record.base_price.toFixed(2)}€
          </div>
          <div>
            <Text type="secondary">+ {record.price_per_km.toFixed(2)}€/km</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Durée estimée",
      dataIndex: "estimated_duration",
      key: "estimated_duration",
      sorter: true,
      render: (duration: number) => (
        <Space>
          <ClockCircleOutlined />
          {missionTypeService.formatDuration(duration)}
        </Space>
      ),
    },
    {
      title: "Véhicules compatibles",
      dataIndex: "vehicle_types",
      key: "vehicle_types",
      render: (types: string[]) => (
        <Space wrap>
          {types.map((type) => (
            <Tag key={type} icon={<CarOutlined />}>
              {missionTypeService.getVehicleTypeLabel(type)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Spécial",
      dataIndex: "requires_special_vehicle",
      key: "requires_special_vehicle",
      render: (requires: boolean) =>
        requires ? (
          <Tag color="orange">Véhicule spécialisé</Tag>
        ) : (
          <Tag color="green">Standard</Tag>
        ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive: boolean) => (
        <Tag
          color={isActive ? "green" : "red"}
          icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}
        >
          {isActive ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menuItems = [
          {
            key: "edit",
            icon: <EditOutlined />,
            label: "Modifier",
            onClick: () => handleEdit(record),
          },
          {
            key: "duplicate",
            icon: <CopyOutlined />,
            label: "Dupliquer",
            onClick: () => handleDuplicate(record.id),
          },
          {
            key: "toggle",
            icon: record.is_active ? <StopOutlined /> : <CheckCircleOutlined />,
            label: record.is_active ? "Désactiver" : "Activer",
            onClick: () => handleToggleStatus(record),
          },
          {
            type: "divider" as const,
          },
          {
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Supprimer",
            danger: true,
            onClick: () => handleDelete(record.id, record.name),
          },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  if (error) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <p>Erreur lors du chargement des types de missions</p>
        <Button onClick={() => refetch()} icon={<ReloadOutlined />}>
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <Space>
            <TagOutlined />
            Types de missions
            <Badge
              count={stats?.total || 0}
              style={{ backgroundColor: "#52c41a" }}
            />
          </Space>
        }
        extra={
          <Space>
            <Button onClick={() => refetch()} icon={<ReloadOutlined />}>
              Actualiser
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              loading={createMutation.isPending}
            >
              Nouveau type
            </Button>
          </Space>
        }
      >
        {/* Statistiques */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: "16px" }}>
            <Col span={8}>
              <div
                style={{
                  textAlign: "center",
                  padding: "16px",
                  backgroundColor: "#f6ffed",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#52c41a",
                  }}
                >
                  {stats.active}
                </div>
                <div style={{ color: "#666" }}>Types actifs</div>
              </div>
            </Col>
            <Col span={8}>
              <div
                style={{
                  textAlign: "center",
                  padding: "16px",
                  backgroundColor: "#fff2e8",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#fa8c16",
                  }}
                >
                  {stats.inactive}
                </div>
                <div style={{ color: "#666" }}>Types inactifs</div>
              </div>
            </Col>
            <Col span={8}>
              <div
                style={{
                  textAlign: "center",
                  padding: "16px",
                  backgroundColor: "#f0f5ff",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#1890ff",
                  }}
                >
                  {stats.total}
                </div>
                <div style={{ color: "#666" }}>Total</div>
              </div>
            </Col>
          </Row>
        )}

        {/* Filtres */}
        <Row gutter={16} style={{ marginBottom: "16px" }}>
          <Col span={12}>
            <Input.Search
              placeholder="Rechercher par nom ou description..."
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: "100%" }}
              onChange={handleStatusFilter}
              allowClear
            >
              <Select.Option value="all">Tous</Select.Option>
              <Select.Option value="active">Actifs</Select.Option>
              <Select.Option value="inactive">Inactifs</Select.Option>
            </Select>
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={missionTypes}
            rowKey="id"
            pagination={{
              current: pagination?.page,
              pageSize: pagination?.limit,
              total: pagination?.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} types de missions`,
            }}
            onChange={handleTableChange}
          />
        </Spin>
      </Card>

      {/* Modal de création/modification */}
      <Modal
        title={
          editingType
            ? "Modifier le type de mission"
            : "Créer un type de mission"
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingType(null);
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="name"
                label="Nom du type de mission"
                rules={[{ required: true, message: "Le nom est requis" }]}
              >
                <Input placeholder="Ex: Transport Standard" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="color"
                label="Couleur"
                rules={[{ required: true, message: "La couleur est requise" }]}
              >
                <ColorPicker showText />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Description détaillée du type de mission"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="base_price" label="Prix de base (€)">
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: "100%" }}
                  placeholder="25.00"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="price_per_km" label="Prix par km (€)">
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: "100%" }}
                  placeholder="0.85"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="estimated_duration"
                label="Durée estimée (minutes)"
              >
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  placeholder="120"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="vehicle_types"
                label="Types de véhicules compatibles"
              >
                <Select
                  mode="multiple"
                  placeholder="Sélectionner les types de véhicules"
                  options={vehicleTypeOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item
                name="requires_special_vehicle"
                label="Véhicule spécialisé"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item
                name="requires_signature"
                label="Signature requise"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item
                name="requires_photo"
                label="Photo requise"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="is_active" label="Statut" valuePropName="checked">
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>

          <Divider>Champs personnalisés</Divider>
          <Form.Item name="custom_fields" initialValue={[]}>
            <CustomFieldBuilder />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Annuler</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingType ? "Modifier" : "Créer"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminMissionTypes;
